// boodaArQuickLook — JS-side wrapper around the native iOS plugin
// that presents a bundled USDZ via QLPreviewController (which has
// the built-in "View in AR" button). WKWebView doesn't intercept
// <a rel="ar"> the way mobile Safari does, so we need the native
// bridge.
//
// On non-iOS (web, Android) every call resolves with a "not
// supported" shape so call sites can fall back without runtime
// platform checks everywhere.

import { Capacitor, registerPlugin } from "@capacitor/core";

const isIOSNative =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

const Native = registerPlugin("BoodaArQuickLook");

const noop = () => Promise.resolve({ supported: false });

export const BoodaArQuickLook = {
  supported: isIOSNative,

  // Open the named file (relative to /public, with or without
  // leading slash — both forms are accepted by the native side).
  // Example: BoodaArQuickLook.present("/img/Alien/alien_idle.usdz")
  // Uses iOS QLPreviewController; no custom UI overlay.
  present: isIOSNative
    ? (filename) => Native.present({ filename: String(filename) })
    : noop,

  // Custom AR view + card-chip overlay. `filenames` is a dict like:
  //   { idle: "/img/Alien/alien_idle.usdz",
  //     super: "/img/Alien/alien_super.usdz",
  //     build: "/img/Alien/alien_build.usdz", ... }
  // Missing per-card entries fall back to idle on the native side
  // so the AR view doesn't break when only some animation files
  // are bundled yet.
  //
  // `balances` is a dict { super: 3, build: 0, ... } of the kid's
  // current spendable card counts. Chips at 0 render dimmed and
  // ignore taps so the kid can't "feed" an animation they don't own
  // the card for. Positive-balance taps emit a "cardTapped" plugin
  // event ({ key }) so JS can call the spend_kid_card RPC.
  //
  // `mode` picks the rendering style:
  //   "preview" (DEFAULT) — virtual stage, no camera feed. The pet
  //     stands on a soft gradient backdrop. Kid can spin/zoom the
  //     model with gestures but doesn't need to scan their room or
  //     grant camera permission. Loads instantly.
  //   "ar" — full ARKit camera feed + world-tracking; the pet is
  //     anchored in world space 0.9m in front of the kid.
  presentInteractive: isIOSNative
    ? (filenames, balances, opts) =>
        Native.presentInteractive({
          filenames: filenames || {},
          balances: balances  || {},
          mode: (opts && opts.mode) || "preview",
        })
    : noop,

  // Push fresh balances into an already-presented AR view so dimmed
  // chips light up (or vice-versa) without dismissing the view.
  // Called after a successful spend_kid_card RPC + the realtime
  // round-trip updates the local pet state.
  updateBalances: isIOSNative
    ? (balances) => Native.updateBalances({ balances: balances || {} })
    : noop,

  // Subscribe to chip taps from the native AR view. Returns a handle
  // with .remove() so callers can clean up on unmount. The handler
  // receives { key } where key is one of super/build/boost/spark/
  // water/dairy. Web/Android: returns an inert handle.
  addCardTappedListener: isIOSNative
    ? (handler) => Native.addListener("cardTapped", handler)
    : () => ({ remove: () => {} }),

  // Inline 3D pet preview — mounts a native UIView ABOVE the WebView
  // at a rect taken from a DOM element's getBoundingClientRect(). No
  // camera permission, no AR, no chip overlay — just an animated
  // idle pet on a soft gradient backdrop the kid can spin with their
  // finger. Use for the Pet tab inline view; pair with
  // useInlinePetEmbed (a React hook that handles rect syncing).
  //
  //   embedPreview({ filename, rect }) → { embedId }
  //     filename: bundle-relative idle USDZ path
  //     rect: { x, y, width, height } in CSS points
  //   updateEmbedRect({ embedId, rect })
  //     Call on scroll/resize so the native view follows its DOM
  //     placeholder.
  //   dismissEmbed({ embedId })
  //     Tear down. Always call on unmount.
  embedPreview: isIOSNative
    ? ({ filename, rect }) => Native.embedPreview({ filename: String(filename), rect })
    : () => Promise.resolve({ embedId: "" }),

  updateEmbedRect: isIOSNative
    ? ({ embedId, rect }) => Native.updateEmbedRect({ embedId, rect })
    : () => Promise.resolve(),

  dismissEmbed: isIOSNative
    ? ({ embedId }) => Native.dismissEmbed({ embedId })
    : () => Promise.resolve(),

  // Subscribe to "tap on the inline 3D pet" events. Handler receives
  // { embedId } so callers can filter when multiple inline pets are
  // mounted (one per kid profile in multi-kid views). Web/Android:
  // inert handle.
  addPetTappedListener: isIOSNative
    ? (handler) => Native.addListener("petTapped", handler)
    : () => ({ remove: () => {} }),
};

export default BoodaArQuickLook;
